import React, { useMemo } from 'react';
import classNames from 'classnames';
import { MultiSelect } from '../../../Controls/Select/MultiSelect';
import { useFilterField } from '../../hooks/useFilterField';
import { useFilterBarContext } from '../../state/FilterBarContext';
import type { SelectMultiFilterConfig } from '../../types';
import type { SelectOptionType } from '../../../Controls/Select/types';

import cn from './styles.module.scss';

export interface MultiSelectFilterControlProps {
  name: string;
  /** Overrides config.placeholder. */
  placeholder?: string;
  /** Render a static label inside the control's prefix slot. Forwarded to
   *  antd's native `prefix` prop on MultiSelect (inherited from BaseSelect). */
  prefix?: React.ReactNode;
}

/**
 * Renders a SELECT_MULTI field's control. Sibling of `SingleSelectFilterControl`.
 *
 * Differences vs the library's default `MultiSelect` chrome:
 *   - `maxTagCount` is computed at render time as the largest N such that
 *     the joined labels of the first N selected items + the trailing "+M"
 *     overflow chip still fit inside `MAX_DISPLAY_CHARS`.
 *   - `maxTagPlaceholder` is forced to `undefined` so antd uses its default
 *     overflow chip ("+N"). The library's default — a label-as-placeholder
 *     pseudo-tag while empty — only makes sense when no other label/prefix is
 *     present, but FilterBar always supplies a `prefix` (the field label).
 *   - `suffixIcon` is forced to `undefined` so antd uses its default chevron.
 *     The library's default — a counter Badge + caret — duplicates the "+N"
 *     overflow chip and adds chrome the FilterBar context doesn't need.
 *   - `classNames.item` adds a tag-background class. The global Select theme
 *     sets `multipleItemBg: 'none'` because the rest of the app uses
 *     `MultiSelect` as a label-as-placeholder picker that needs a transparent
 *     tag background; FilterBar's use is the opposite — chips need a visible
 *     fill — so the background is re-introduced locally via this className.
 */

const MAX_DISPLAY_CHARS = 24;

export const MultiSelectFilterControl: React.FC<MultiSelectFilterControlProps> = ({
  name,
  placeholder: placeholderOverride,
  prefix,
}) => {
  const { value, options, setValue, config, isLoading } = useFilterField(name);
  const { size } = useFilterBarContext();

  const selectedValues = useMemo(
    () => (value as Array<string | number> | undefined) ?? [],
    [value],
  );

  const maxTagCount = useMemo(
    () => computeVisibleTagCount(selectedValues, options, MAX_DISPLAY_CHARS),
    [selectedValues, options],
  );

  if (!config || config.type !== 'SELECT_MULTI') return null;

  const cfg = config as SelectMultiFilterConfig;
  const size_ = cfg.size ?? size;
  const placeholder = placeholderOverride ?? cfg.placeholder;
  const { classNames: userClassNames, ...restControlProps } = (cfg.controlProps ?? {}) as
    Record<string, unknown> & { classNames?: { item?: string } };
  const userClassNamesObj = (typeof userClassNames === 'object' && userClassNames !== null)
    ? userClassNames
    : {};

  return (
    <MultiSelect
      {...restControlProps}
      multiple
      options={options}
      value={selectedValues}
      onChange={(v) => setValue(v)}
      size={size_}
      placeholder={placeholder}
      prefix={prefix}
      showSelectAllOption={cfg.showSelectAll}
      loading={isLoading}
      disabled={isLoading || (restControlProps as { disabled?: boolean }).disabled}
      debounceTime={cfg.debounceMs}
      showSearch={cfg.searchable !== false}
      allowClear={cfg.clearable !== false}
      maxTagCount={maxTagCount}
      maxTagPlaceholder={undefined}
      suffixIcon={undefined}
      classNames={{
        ...userClassNamesObj,
        item: classNames(cn.tagItem, userClassNamesObj.item),
      }}
    />
  );
};

/**
 * Greedy-fit the selected labels into the char budget; the first N labels that
 * fit FULLY become visible tags. The remainder is implicitly antd's overflow
 * chip ("+(total - shown)"). Reserves room for the trailing "+M" so it never
 * pushes the last visible tag out.
 *
 * Degenerate case: when even the first label exceeds the budget, force at
 * least one visible tag — otherwise the field collapses to a bare "+N" chip
 * with no context for what's selected.
 *
 * Exported for unit testing; runtime callers should go through the component.
 */
export function computeVisibleTagCount(
  values: Array<string | number>,
  options: SelectOptionType[],
  maxChars: number,
): number {
  const total = values.length;
  if (total === 0) return 0;

  let shown = 0;
  let joinedLen = 0;
  for (let i = 0; i < total; i++) {
    const opt = findOption(options, values[i]);
    const label = String(opt?.label ?? values[i]);
    const candidateLen = i === 0 ? label.length : joinedLen + 2 + label.length;
    const remainingAfter = total - i - 1;
    const suffixLen = remainingAfter > 0 ? ` +${remainingAfter}`.length : 0;
    if (candidateLen + suffixLen <= maxChars) {
      joinedLen = candidateLen;
      shown = i + 1;
    } else {
      break;
    }
  }
  return Math.max(shown, 1);
}

function findOption(
  options: SelectOptionType[],
  value: string | number,
): SelectOptionType | undefined {
  for (const o of options) {
    if (o.value === value) return o;
    if (o.options) {
      const sub = findOption(o.options, value);
      if (sub) return sub;
    }
    if (o.children) {
      const sub = findOption(o.children as SelectOptionType[], value);
      if (sub) return sub;
    }
  }
  return undefined;
}
